'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'

import { type ColumnDef, type Row, flexRender } from '@tanstack/react-table'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ArrowUpRight,
  Building2,
  Calendar,
  Clock,
  DollarSign,
  FileText,
  HelpCircle,
  Link2,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Tag,
  User,
  Users,
  Wallet,
} from 'lucide-react'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'

import { Badge } from '@/components/common/BadgeV2'
import Button from '@/components/common/Button'
import { ColumnSelector } from '@/components/common/ColumnSelector'
import { DataTableV2, getGridTemplate } from '@/components/common/DataTableV2'
import Spinner from '@/components/common/Spinner'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { RecipientLink } from '@/components/usaspending/RecipientLink'

import type {
  ApiIdvActivityResponse,
  ApiOrderItem,
} from '@/context/usaspending'

import { useViewTransitionScope } from '@/helpers/viewTransitions/scope'

import { useRenderNowMs } from '@/hooks/common/useRenderNowMs'
import {
  type ColumnDefinition,
  useColumnPreferences,
} from '@/hooks/useColumnPreferences'

import {
  formatSubawardType,
  getActionTypeLabels,
  getTypeLabels,
} from '@/lib/usaspending/awardTypeMappings'
import { selectRecipientLevel } from '@/lib/usaspending/recipientId'

import type {
  Award,
  Solicitation,
  SubAward,
  Transaction,
} from '@/types/usaspending'

import { PlaceOfPerformanceV2, ShareIcon } from '../../common'
import { BadgeV2 } from '../../common/BadgeV2'
import { CardV2 } from '../../common/CardV2'
import { DataBlockV2 } from '../../common/DataBlockV2'
import { useTitleClasses } from '../../common/hooks'
import { SidebarWidgetV2 } from '../../common/SidebarWidgetV2'
import { TabPanelV2 } from '../../common/TabPanelV2'
import { TimelineV2 } from '../../common/TimelineV2'
import {
  formatBusinessType,
  formatCompactCurrency,
  formatCurrency,
  formatDate,
} from '../../common/transforms'
import { getCategoryConfig } from './categoryConfig'
import { DisasterFundingCard, hasDefcData } from './DisasterFundingCard'
import { FundingGaugeCard } from './FundingGauge'
import type { ExtendedFinancialData } from './FundingGauge'
import {
  IdvActivitySankey,
  getMissingDataInfo,
  hasActivityData,
} from './IdvActivitySankey'
import { ParentIdvCard, hasParentIdvData } from './ParentIdvCard'
import {
  ProcurementDetailsPanel,
  hasProcurementData,
} from './ProcurementDetailsPanel'
import {
  ProgramDetailsPanel,
  hasProgramData,
  isAssistanceCategory,
} from './ProgramDetailsPanel'
import {
  SubAwardPreviewPopover,
  SubAwardPreviewTrigger,
} from './SubAwardPreviewPopover'
import { normalizeAwardData } from './transforms'

// ============================================================================
// Types
// ============================================================================

interface AwardDisplayV2Props {
  award: Award
  /**
   * Compact mode shows a simplified view suitable for search results.
   * Shows: badges, title, data grid, description
   * Hides: tabs, sidebar, detailed tables
   */
  isCompact?: boolean
  /** Called when user clicks to expand from compact to full view */
  onExpandToFull?: () => void
  /** Called when user clicks to collapse from full to compact view */
  onCollapseToCompact?: () => void
  /** Optional solicitation data loaded separately */
  solicitationData?: Solicitation | null
  isLoadingSolicitation?: boolean
  /** Transactions loaded separately (paginated) */
  transactions?: Transaction[]
  /** Total number of transactions (from API, not loaded count) */
  totalTransactions?: number
  isLoadingTransactions?: boolean
  isFetchingMoreTransactions?: boolean
  hasMoreTransactions?: boolean
  onLoadMoreTransactions?: () => void
  onLoadAllTransactions?: () => Promise<void>
  isTransactionsFullyLoaded?: boolean
  /** SubAwards loaded separately (paginated) */
  subAwards?: SubAward[]
  /** Total number of subawards (from API, not loaded count) */
  totalSubAwards?: number
  isLoadingSubAwards?: boolean
  isFetchingMoreSubAwards?: boolean
  hasMoreSubAwards?: boolean
  onLoadMoreSubAwards?: () => void
  onLoadAllSubAwards?: () => Promise<void>
  isSubAwardsFullyLoaded?: boolean
  /** Orders (child awards) loaded separately for IDVs (paginated) */
  orders?: ApiOrderItem[]
  /** Total number of orders (from API, not loaded count) */
  totalOrders?: number
  isLoadingOrders?: boolean
  isFetchingMoreOrders?: boolean
  hasMoreOrders?: boolean
  onLoadMoreOrders?: () => void
  onLoadAllOrders?: () => Promise<void>
  isOrdersFullyLoaded?: boolean
  /** IDV child order aggregation (obligations/outlays from all orders) */
  idvChildAggregation?: {
    obligated: number
    outlayed: number
    count: number
  }
  isLoadingIdvAggregation?: boolean
  /** IDV activity data for Sankey visualization */
  idvActivity?: ApiIdvActivityResponse
  isLoadingIdvActivity?: boolean
  onShare?: () => void
}

// ============================================================================
// Category-specific Data Grid Helpers
// ============================================================================

/**
 * Get the primary amount to display based on award category.
 * Pure function - same inputs always produce same output.
 *
 * @param category - Award category (loans, grant, idv, contract, etc.)
 * @param financials - Extended financial data from award
 * @returns Primary amount for display in header data grid
 */
function getPrimaryAmount(
  category: string | null | undefined,
  financials: ExtendedFinancialData,
): number {
  const cat = category?.toLowerCase()

  switch (cat) {
    case 'loans':
      return financials.totalLoanValue ?? 0
    case 'direct payment':
      return financials.totalOutlayedAmount ?? 0
    case 'idv':
      return (
        financials.baseAndAllOptionsValue ??
        financials.potentialAwardAmount ??
        0
      )
    case 'grant':
    case 'contract':
    default:
      return financials.obligatedAmount ?? 0
  }
}

/**
 * Get the subtext for primary amount based on award category.
 * Pure function - same inputs always produce same output.
 *
 * @param category - Award category
 * @param financials - Extended financial data from award
 * @param percentObligated - Pre-calculated obligation percentage
 * @returns Descriptive subtext for primary amount
 */
function getPrimaryAmountSubtext(
  category: string | null | undefined,
  financials: ExtendedFinancialData,
  percentObligated: number,
): string {
  const cat = category?.toLowerCase()

  switch (cat) {
    case 'loans': {
      const subsidy = financials.totalSubsidyCost ?? 0
      return subsidy > 0
        ? `${formatCompactCurrency(subsidy)} federal subsidy`
        : 'Face value'
    }
    case 'direct payment':
      return 'Total disbursements'
    case 'idv':
      return 'Maximum potential value'
    case 'grant': {
      const obligated = financials.obligatedAmount ?? 0
      const outlayed = financials.totalOutlayedAmount ?? 0
      const pctDisbursed = obligated > 0 ? (outlayed / obligated) * 100 : 0
      return `${pctDisbursed.toFixed(0)}% disbursed`
    }
    case 'contract':
    default:
      return `${percentObligated.toFixed(0)}% of potential`
  }
}

// ============================================================================
// ObligationMismatchTooltip - Pure component for obligation data discrepancy
// ============================================================================

interface ObligationMismatchTooltipProps {
  summary: ApiIdvActivityResponse['summary']
}

/**
 * Displays a help icon with tooltip when reported obligation differs
 * significantly (>5%) from actual computed obligation.
 *
 * Pure component - renders based solely on props, no side effects.
 */
function ObligationMismatchTooltip({
  summary,
}: ObligationMismatchTooltipProps) {
  const { hasObligationMismatch } = getMissingDataInfo(summary)

  if (!hasObligationMismatch) return null

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="ml-1 inline-flex items-center align-middle text-amber-500 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300"
          aria-label="Obligation data note"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-64 px-3 py-4" side="right" inverseColors>
        <div className="space-y-2 text-xs leading-relaxed">
          <p>
            While USASpending reports{' '}
            <span className="font-semibold">
              {formatCompactCurrency(summary.rollup_total_obligation)}
            </span>
            , the orders shown total{' '}
            <span className="font-semibold">
              {formatCompactCurrency(summary.actual_total_obligation)}
            </span>
            .
          </p>
          <p>This difference may be due to missing historical transactions.</p>
          <p>Our federal spending data coverage is improving daily.</p>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

// ============================================================================
// FederalAccountsCard - Display federal accounts with integrated header
// ============================================================================

interface FederalAccountsCardProps {
  accounts: Array<{
    id: string
    title: string
    agencyIdentifier: string
    mainAccountCode: string
    accountType: string
  }>
}

const FederalAccountsCard: React.FC<FederalAccountsCardProps> = ({
  accounts,
}) => {
  if (accounts.length === 0) {
    return (
      <CardV2 variant="accent">
        <CardV2.Header>
          <CardV2.HeaderIcon>
            <Wallet />
          </CardV2.HeaderIcon>
          <CardV2.HeaderTitle>Federal Accounts</CardV2.HeaderTitle>
        </CardV2.Header>
        <CardV2.Content>
          <div className="py-4 text-center text-sm text-primary-500 dark:text-primary-200">
            No federal accounts available for this award.
          </div>
        </CardV2.Content>
      </CardV2>
    )
  }

  return (
    <CardV2 variant="accent">
      <CardV2.Header>
        <CardV2.HeaderIcon>
          <Wallet />
        </CardV2.HeaderIcon>
        <CardV2.HeaderTitle>Federal Accounts</CardV2.HeaderTitle>
        <CardV2.HeaderBadge>{accounts.length} Accounts</CardV2.HeaderBadge>
      </CardV2.Header>
      <CardV2.Content flush>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[500px] text-left text-sm">
            <thead className="border-b border-primary-200 text-xs font-semibold uppercase tracking-wide text-primary-400 dark:border-primary-700 dark:text-primary-500">
              <tr>
                <th className="whitespace-nowrap px-4 py-3">Account Title</th>
                <th className="whitespace-nowrap px-4 py-3">Agency ID</th>
                <th className="whitespace-nowrap px-4 py-3">Main Account</th>
                <th className="whitespace-nowrap px-4 py-3">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-primary-100 dark:divide-primary-800">
              {accounts.map((acc) => (
                <tr
                  key={acc.id}
                  className="transition-colors hover:bg-primary-50 dark:hover:bg-primary-800"
                >
                  <td className="px-4 py-3 font-medium text-primary-900 dark:text-primary-100">
                    {acc.title || '-'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-primary-600 dark:text-primary-200">
                    {acc.agencyIdentifier || '-'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-primary-600 dark:text-primary-200">
                    {acc.mainAccountCode || '-'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-primary-600 dark:text-primary-200">
                    {acc.accountType || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardV2.Content>
    </CardV2>
  )
}

// ============================================================================
// Column Metadata Constants (for ColumnSelector)
// ============================================================================

/**
 * Transaction column metadata for ColumnSelector.
 * Defined as stable constants outside component for referential stability.
 */
const TRANSACTION_BASE_COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'modNumber', label: 'Mod #' },
  { id: 'actionDate', label: 'Date' },
  { id: 'actionType', label: 'Type' },
]

const TRANSACTION_PROCUREMENT_COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'typeOfContractPricingDescription', label: 'Contract Type' },
  { id: 'typeSetAsideDescription', label: 'Set-Aside' },
  { id: 'extentCompetedDescription', label: 'Competed' },
  { id: 'naicsPsc', label: 'NAICS/PSC' },
  { id: 'awardingOfficeName', label: 'Awarding Office' },
]

const TRANSACTION_END_COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'federalActionObligation', label: 'Amount' },
  { id: 'description', label: 'Description' },
]

/**
 * Get all transaction column definitions based on category.
 * Pure function - same inputs always produce same output.
 */
function getTransactionColumnDefs(
  showProcurementColumns: boolean,
): ColumnDefinition[] {
  if (showProcurementColumns) {
    return [
      ...TRANSACTION_BASE_COLUMN_DEFS,
      ...TRANSACTION_PROCUREMENT_COLUMN_DEFS,
      ...TRANSACTION_END_COLUMN_DEFS,
    ]
  }
  return [...TRANSACTION_BASE_COLUMN_DEFS, ...TRANSACTION_END_COLUMN_DEFS]
}

/**
 * SubAward column metadata for ColumnSelector.
 */
const SUBAWARD_COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'date', label: 'Date' },
  { id: 'amount', label: 'Amount' },
  { id: 'subawardType', label: 'Type' },
  { id: 'subaward_number', label: 'Subaward #' },
  { id: 'recipient', label: 'Recipient' }, // Label updated dynamically
]

/**
 * Order (child award) column metadata for ColumnSelector.
 */
const ORDER_COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'display_award_id', label: 'Award ID' },
  { id: 'recipient_name', label: 'Recipient' },
  { id: 'action_date', label: 'Date' },
  { id: 'award_amount', label: 'Amount' },
  { id: 'type_description', label: 'Type' },
]

// ============================================================================
// Transaction Column Definitions (Pure, Memoizable)
// ============================================================================

/**
 * Creates transaction column definitions.
 * Defined as a factory function outside component for referential stability.
 *
 * @param showProcurementColumns - Whether to include FPDS-specific columns (contracts/IDVs)
 */
function createTransactionColumns(
  showProcurementColumns: boolean,
): ColumnDef<Transaction, unknown>[] {
  const baseColumns: ColumnDef<Transaction, unknown>[] = [
    {
      accessorKey: 'modNumber',
      header: 'Mod #',
      cell: ({ getValue }) => (
        <span className="whitespace-nowrap font-mono text-xs font-medium text-primary-900 dark:text-primary-100">
          {(getValue() as string) || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'actionDate',
      header: 'Date',
      size: 115,
      cell: ({ getValue }) => (
        <span className="whitespace-nowrap text-primary-600 dark:text-primary-200">
          {formatDate(getValue() as string) || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'actionType',
      header: 'Type',
      size: 160,
      cell: ({ getValue }) => {
        const rawType = getValue() as string
        // Action types have different meanings for procurement vs assistance
        const labels = getActionTypeLabels(rawType, showProcurementColumns)
        if (!labels.short) return <span className="text-primary-400">—</span>
        return (
          <Badge
            variant="outline"
            className="whitespace-nowrap text-xs"
            title={labels.full ?? undefined}
          >
            {labels.short}
          </Badge>
        )
      },
    },
  ]

  // Procurement-specific columns (FPDS contracts/IDVs only)
  const procurementColumns: ColumnDef<Transaction, unknown>[] =
    showProcurementColumns
      ? [
          {
            accessorKey: 'typeOfContractPricingDescription',
            header: 'Contract Type',
            size: 150,
            cell: ({ getValue }) => {
              const value = getValue() as string | undefined
              if (!value) return <span className="text-primary-400">—</span>
              return (
                <span
                  className="line-clamp-2 text-xs text-primary-600 dark:text-primary-200"
                  title={value}
                >
                  {value}
                </span>
              )
            },
          },
          {
            accessorKey: 'typeSetAsideDescription',
            header: 'Set-Aside',
            size: 140,
            cell: ({ getValue }) => {
              const value = getValue() as string | undefined
              if (!value) return <span className="text-primary-400">—</span>
              return (
                <span
                  className="line-clamp-2 text-xs text-primary-600 dark:text-primary-200"
                  title={value}
                >
                  {value}
                </span>
              )
            },
          },
          {
            accessorKey: 'extentCompetedDescription',
            header: 'Competed',
            size: 150,
            cell: ({ getValue }) => {
              const value = getValue() as string | undefined
              if (!value) return <span className="text-primary-400">—</span>
              return (
                <span
                  className="line-clamp-2 text-xs text-primary-600 dark:text-primary-200"
                  title={value}
                >
                  {value}
                </span>
              )
            },
          },
          {
            id: 'naicsPsc',
            header: 'NAICS/PSC',
            size: 110,
            cell: ({ row }) => {
              const naics = row.original.naicsCode
              const psc = row.original.productOrServiceCode
              if (!naics && !psc)
                return <span className="text-primary-400">—</span>
              const combined = [naics, psc].filter(Boolean).join(' / ')
              return (
                <span
                  className="whitespace-nowrap font-mono text-xs text-primary-600 dark:text-primary-200"
                  title={combined}
                >
                  {combined}
                </span>
              )
            },
          },
          {
            accessorKey: 'awardingOfficeName',
            header: 'Awarding Office',
            size: 180,
            cell: ({ getValue }) => {
              const value = getValue() as string | undefined
              if (!value) return <span className="text-primary-400">—</span>
              return (
                <span
                  className="line-clamp-2 text-xs text-primary-600 dark:text-primary-200"
                  title={value}
                >
                  {value}
                </span>
              )
            },
          },
        ]
      : []

  const endColumns: ColumnDef<Transaction, unknown>[] = [
    {
      accessorKey: 'federalActionObligation',
      header: 'Amount',
      size: 130,
      // Ensure numeric sorting (handles negatives correctly)
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.getValue(columnId) as number | null
        const b = rowB.getValue(columnId) as number | null
        // Nulls go last
        if (a == null && b == null) return 0
        if (a == null) return 1
        if (b == null) return -1
        return a - b
      },
      cell: ({ getValue }) => {
        const amount = getValue() as number
        return (
          <span
            className={twMerge(
              'whitespace-nowrap font-mono',
              amount >= 0
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-rose-600 dark:text-rose-400',
            )}
          >
            {formatCurrency(amount) || '—'}
          </span>
        )
      },
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: 'Description',
      size: 350,
      minSize: 250,
      cell: ({ getValue }) => {
        const desc = getValue() as string
        return (
          <p className="text-xs leading-snug text-primary-500 dark:text-primary-200">
            {desc || '—'}
          </p>
        )
      },
    },
  ]

  return [...baseColumns, ...procurementColumns, ...endColumns]
}

// ============================================================================
// Transaction Row Component (Pure, Memoized)
// ============================================================================

interface TransactionRowProps {
  row: Row<Transaction>
}

/**
 * TransactionRow - renders a single transaction row
 * Note: Not using React.memo because row.getVisibleCells() needs to be called
 * on each render to reflect column visibility/order changes from TanStack Table.
 */
function TransactionRow({ row }: TransactionRowProps) {
  const cells = row.getVisibleCells()
  // Use getGridTemplate to match header column widths (uses column.size values)
  const gridTemplate = getGridTemplate(cells.map((c) => c.column))

  return (
    <div
      className="grid border-b border-primary-100 transition-colors hover:bg-primary-50 dark:border-primary-800 dark:hover:bg-primary-800"
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {cells.map((cell) => (
        <div key={cell.id} className="min-w-0 px-4 py-3 text-sm">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// SubAward Column Definitions (Pure, Memoizable)
// ============================================================================

/**
 * Creates subaward column definitions.
 * Defined as a factory function outside component for referential stability.
 *
 * @param subawardeeLabel - Category-aware label for the subawardee column
 * @param category - Award category for category-aware terminology in popover
 */
function createSubAwardColumns(
  subawardeeLabel: string,
  category?: string | null,
): ColumnDef<SubAward, unknown>[] {
  return [
    // Fixed columns first
    {
      accessorKey: 'date',
      header: 'Date',
      size: 115,
      cell: ({ getValue }) => (
        <span className="whitespace-nowrap text-primary-600 dark:text-primary-200">
          {formatDate(getValue() as string) || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      size: 130,
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.getValue(columnId) as number | null
        const b = rowB.getValue(columnId) as number | null
        if (a == null && b == null) return 0
        if (a == null) return 1
        if (b == null) return -1
        return a - b
      },
      cell: ({ getValue }) => (
        <span className="whitespace-nowrap font-mono text-emerald-600 dark:text-emerald-400">
          {formatCurrency(getValue() as number) || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'subawardType',
      header: 'Type',
      size: 140,
      cell: ({ getValue }) => {
        const rawType = getValue() as string | undefined
        const formattedType = formatSubawardType(rawType)
        if (!formattedType) return <span className="text-primary-400">—</span>
        return (
          <Badge variant="outline" className="whitespace-nowrap text-xs">
            {formattedType}
          </Badge>
        )
      },
    },
    {
      id: 'subaward_number',
      accessorKey: 'id',
      header: 'Subaward #',
      size: 220,
      cell: ({ row }) => {
        const subAward = row.original
        const hasLocation = !!(
          subAward.subAwardee?.location?.city ||
          subAward.subAwardee?.location?.state
        )
        const hasPlaceOfPerformance = !!(
          subAward.placeOfPerformance?.city ||
          subAward.placeOfPerformance?.state
        )
        const hasUei = !!subAward.subAwardee?.uei
        const hasBusinessTypes =
          (subAward.subAwardee?.businessTypes?.length ?? 0) > 0
        const hasSubawardType = !!subAward.subawardType
        const hasNaics = !!subAward.naicsCode
        const hasDescription = !!subAward.description?.trim()
        const hasDetailPage = !!subAward.subawardId

        const hasPreview =
          hasDetailPage ||
          hasLocation ||
          hasPlaceOfPerformance ||
          hasUei ||
          hasBusinessTypes ||
          hasSubawardType ||
          hasNaics ||
          hasDescription

        if (!hasPreview) {
          return (
            <SubAwardPreviewTrigger
              subawardNumber={subAward.id}
              hasPreview={false}
            />
          )
        }

        return (
          <SubAwardPreviewPopover subAward={subAward} category={category}>
            <SubAwardPreviewTrigger
              subawardNumber={subAward.id}
              hasPreview={true}
            />
          </SubAwardPreviewPopover>
        )
      },
    },
    // Recipient column - placed last, fits content width
    {
      id: 'recipient',
      accessorKey: 'subAwardee.name',
      header: subawardeeLabel,
      size: 250,
      cell: ({ row }) => {
        const subAwardee = row.original.subAwardee
        if (!subAwardee) {
          return <span className="text-primary-400">—</span>
        }
        if (subAwardee.id) {
          return (
            <RecipientLink
              hash={subAwardee.id}
              className="whitespace-nowrap text-accent-600 hover:text-blue-600 hover:underline dark:text-accent-400 dark:hover:text-blue-400"
            >
              {subAwardee.name}
            </RecipientLink>
          )
        }
        return (
          <span className="whitespace-nowrap text-primary-900 dark:text-primary-100">
            {subAwardee.name}
          </span>
        )
      },
    },
  ]
}

// ============================================================================
// SubAward Row Component (Pure, Memoized)
// ============================================================================

interface SubAwardRowProps {
  row: Row<SubAward>
}

function SubAwardRow({ row }: SubAwardRowProps) {
  const cells = row.getVisibleCells()
  const gridTemplate = getGridTemplate(cells.map((c) => c.column))

  return (
    <div
      className="grid border-b border-primary-100 transition-colors hover:bg-primary-50 dark:border-primary-800 dark:hover:bg-primary-800"
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {cells.map((cell) => (
        <div key={cell.id} className="min-w-0 px-4 py-3 text-sm">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// Order Column Definitions (Pure, Memoizable)
// ============================================================================

/**
 * Creates order (child award) column definitions.
 * Defined as a factory function outside component for referential stability.
 * Returns memoizable columns that can be wrapped in useMemo with empty deps.
 */
function createOrderColumns(): ColumnDef<ApiOrderItem, unknown>[] {
  return [
    {
      accessorKey: 'display_award_id',
      header: 'Award ID',
      size: 175,
      cell: ({ row }) => {
        const order = row.original
        return (
          <Link
            href={`/awards/usaspending/${encodeURIComponent(order.generated_unique_award_id)}`}
            className="font-mono text-xs font-medium text-accent-600 hover:text-blue-600 hover:underline dark:text-accent-400 dark:hover:text-blue-400"
          >
            {order.display_award_id}
          </Link>
        )
      },
    },
    {
      accessorKey: 'recipient_name',
      header: 'Recipient',
      size: 220,
      cell: ({ row }) => {
        const order = row.original
        if (order.recipient_hash) {
          return (
            <RecipientLink
              hash={order.recipient_hash}
              level={selectRecipientLevel(
                order.recipient_levels,
                order.parent_uei,
              )}
              className="leading-snug text-accent-600 hover:text-blue-600 hover:underline dark:text-accent-400 dark:hover:text-blue-400"
            >
              {order.recipient_name || '—'}
            </RecipientLink>
          )
        }
        return (
          <span className="leading-snug text-primary-900 dark:text-primary-100">
            {order.recipient_name || '—'}
          </span>
        )
      },
    },
    {
      accessorKey: 'action_date',
      header: 'Date',
      size: 115,
      cell: ({ getValue }) => (
        <span className="whitespace-nowrap text-primary-600 dark:text-primary-200">
          {formatDate(getValue() as string) || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'award_amount',
      header: 'Amount',
      size: 130,
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.getValue(columnId) as number | null
        const b = rowB.getValue(columnId) as number | null
        if (a == null && b == null) return 0
        if (a == null) return 1
        if (b == null) return -1
        return a - b
      },
      cell: ({ getValue }) => (
        <span className="whitespace-nowrap font-mono text-emerald-600 dark:text-emerald-400">
          {formatCurrency(getValue() as number) || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'type_description',
      header: 'Type',
      size: 160,
      cell: ({ getValue, row }) => {
        const typeCode = row.original.type
        const labels = getTypeLabels(typeCode)
        // Fall back to type_description from API if no mapping
        const typeDescription = getValue() as string
        const shortLabel = labels.short || typeDescription
        const fullLabel = labels.full || typeDescription
        if (!shortLabel) return <span className="text-primary-400">—</span>
        return (
          <Badge
            variant="outline"
            className="whitespace-nowrap text-xs"
            title={fullLabel ?? undefined}
          >
            {shortLabel}
          </Badge>
        )
      },
    },
  ]
}

// ============================================================================
// Order Row Component (Pure, Memoized)
// ============================================================================

interface OrderRowProps {
  row: Row<ApiOrderItem>
}

function OrderRow({ row }: OrderRowProps) {
  const cells = row.getVisibleCells()
  const gridTemplate = getGridTemplate(cells.map((c) => c.column))

  return (
    <div
      className="grid border-b border-primary-100 transition-colors hover:bg-primary-50 dark:border-primary-800 dark:hover:bg-primary-800"
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {cells.map((cell) => (
        <div key={cell.id} className="min-w-0 px-4 py-3 text-sm">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

const AwardDisplayV2: React.FC<AwardDisplayV2Props> = ({
  award,
  isCompact = false,
  onExpandToFull,
  onCollapseToCompact,
  solicitationData,
  isLoadingSolicitation = false,
  transactions = [],
  totalTransactions = 0,
  isLoadingTransactions = false,
  isFetchingMoreTransactions = false,
  hasMoreTransactions = false,
  onLoadMoreTransactions,
  onLoadAllTransactions,
  isTransactionsFullyLoaded = false,
  subAwards = [],
  totalSubAwards = 0,
  isLoadingSubAwards = false,
  isFetchingMoreSubAwards = false,
  hasMoreSubAwards = false,
  onLoadMoreSubAwards,
  onLoadAllSubAwards,
  isSubAwardsFullyLoaded = false,
  orders = [],
  totalOrders = 0,
  isLoadingOrders = false,
  isFetchingMoreOrders = false,
  hasMoreOrders = false,
  onLoadMoreOrders,
  onLoadAllOrders,
  isOrdersFullyLoaded = false,
  idvChildAggregation,
  isLoadingIdvAggregation = false,
  idvActivity,
  isLoadingIdvActivity = false,
  onShare,
}) => {
  const referenceNowMs = useRenderNowMs({ refreshMs: 60_000 })
  // Award detail is live client data, so fall back to the local clock when
  // no SSR/ISR render stamp is available.
  const data = useMemo(
    () => normalizeAwardData(award, referenceNowMs ?? Date.now()),
    [award, referenceNowMs],
  )
  const categoryConfig = getCategoryConfig(data.stats.category)
  const [activeTab, setActiveTab] = useState('overview')
  const [businessTypesExpanded, setBusinessTypesExpanded] = useState(false)

  // Memoized column definitions - stable references for TanStack Table
  // NOTE: Category-based columns are acceptable since category doesn't change for a given award.
  const isProcurement =
    data.stats.category === 'contract' || data.stats.category === 'idv'

  // Column metadata for ColumnSelector (stable across renders)
  const transactionColumnDefs = useMemo(
    () => getTransactionColumnDefs(isProcurement),
    [isProcurement],
  )
  const subAwardColumnDefs = useMemo(
    () =>
      SUBAWARD_COLUMN_DEFS.map((col) =>
        col.id === 'recipient'
          ? { ...col, label: categoryConfig.subawardeeLabel }
          : col,
      ),
    [categoryConfig.subawardeeLabel],
  )

  // Column preferences with localStorage persistence
  const transactionPreferences = useColumnPreferences(
    'award-transactions',
    transactionColumnDefs,
  )
  const subAwardPreferences = useColumnPreferences(
    'award-subawards',
    subAwardColumnDefs,
  )
  const orderPreferences = useColumnPreferences(
    'award-orders',
    ORDER_COLUMN_DEFS,
  )

  // Column definitions (pass to TanStack Table with columnOrder/columnVisibility state)
  const transactionColumns = useMemo(
    () => createTransactionColumns(isProcurement),
    [isProcurement],
  )
  const subAwardColumns = useMemo(
    () =>
      createSubAwardColumns(
        categoryConfig.subawardeeLabel,
        data.stats.category,
      ),
    [categoryConfig.subawardeeLabel, data.stats.category],
  )
  const orderColumns = useMemo(() => createOrderColumns(), [])

  // Track if we've completed loading solicitation at least once
  const hasFinishedLoadingOnce = useRef(false)

  // Handle solicitation tab state when loading completes
  useEffect(() => {
    if (!isLoadingSolicitation) {
      hasFinishedLoadingOnce.current = true
      // If we're on the solicitation tab but there's no solicitation data, switch back
      if (
        activeTab === 'solicitation' &&
        !data.solicitation &&
        !solicitationData
      ) {
        setActiveTab('overview')
      }
    }
  }, [isLoadingSolicitation, data.solicitation, solicitationData, activeTab])

  // Determine if we have solicitation to show
  const hasSolicitation = data.solicitation || solicitationData

  // Shared hooks
  const titleClasses = useTitleClasses(data.title)

  // View transition scope prevents collisions when the same award is rendered
  // in multiple UI surfaces at once (e.g., full page + floating terminal).
  const vtScope = useViewTransitionScope()

  // View transition id for shared title animation between compact card and the
  // full detail view. We prefer the stable USASpending identifier.
  const vtAwardId: string | undefined =
    (award as any)?.generated_unique_award_id ?? (award as any)?.id

  return (
    <>
      {/* Header Block */}
      <div className={isCompact ? 'mb-3' : 'mb-6'}>
        <div
          className={isCompact ? 'flex flex-col gap-2' : 'flex flex-col gap-3'}
        >
          {/* Badges Row */}
          <div
            className={
              isCompact
                ? 'flex flex-wrap items-center gap-1.5'
                : 'flex flex-wrap items-center gap-2'
            }
          >
            {data.badges.map((badge) => (
              <BadgeV2
                key={`${badge.variant}-${badge.label}`}
                variant={badge.variant}
                size={isCompact ? 'sm' : 'md'}
              >
                {badge.label}
              </BadgeV2>
            ))}
          </div>

          {/* Title + Actions Row */}
          <div
            className={
              isCompact
                ? 'flex flex-col justify-between gap-2 sm:flex-row sm:items-start'
                : 'flex flex-col justify-between gap-4 lg:flex-row lg:items-end'
            }
          >
            <div
              className={
                isCompact ? 'max-w-full space-y-1' : 'max-w-4xl space-y-1.5'
              }
            >
              <h1
                style={
                  vtAwardId
                    ? ({
                        // Namespace to avoid collisions with any other award UI surfaces
                        // (e.g., the floating terminal) that might also render the same
                        // award title at the same time.
                        viewTransitionName: `award-title-${vtScope}-${vtAwardId}`,
                      } as React.CSSProperties)
                    : undefined
                }
                className={twMerge(
                  'line-clamp-3',
                  isCompact
                    ? 'text-base font-semibold leading-tight'
                    : titleClasses,
                  'text-primary-900 dark:text-primary-50',
                )}
              >
                {data.title}
              </h1>
              <div
                className={
                  isCompact
                    ? 'flex items-center gap-1.5 text-xs text-primary-500'
                    : 'flex items-center gap-2 text-sm text-primary-500'
                }
              >
                <Building2
                  className={
                    isCompact
                      ? 'h-3 w-3 text-primary-400'
                      : 'h-4 w-4 text-primary-400'
                  }
                />
                <span className="text-primary-600 dark:text-primary-200">
                  {data.subtitle}
                </span>
              </div>
              {/* Award IDs - hidden in compact mode */}
              {!isCompact && (
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  {data.piid && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold uppercase tracking-widest text-primary-400">
                        PIID
                      </span>
                      <span className="rounded border border-accent-200 bg-accent-50 px-1.5 py-0.5 font-mono text-xs text-accent-700 dark:border-accent-700 dark:bg-accent-900/30 dark:text-accent-300">
                        {data.piid}
                      </span>
                    </div>
                  )}
                  {data.fain && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold uppercase tracking-widest text-primary-400">
                        FAIN
                      </span>
                      <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 font-mono text-xs text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                        {data.fain}
                      </span>
                    </div>
                  )}
                  {data.parentAwardGeneratedUniqueId && (
                    <div className="flex items-center gap-1.5">
                      <Link2 className="h-3 w-3 text-primary-400" />
                      <span className="text-xs font-semibold uppercase tracking-widest text-primary-400">
                        Parent IDV
                      </span>
                      <Link
                        href={`/awards/usaspending/${encodeURIComponent(data.parentAwardGeneratedUniqueId)}`}
                        className="group inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 font-mono text-xs text-amber-700 transition-colors hover:border-amber-300 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:border-amber-600 dark:hover:bg-amber-900/50"
                      >
                        {data.parentAwardPiid}
                        <ArrowUpRight className="h-3 w-3 opacity-60 transition-opacity group-hover:opacity-100" />
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {!isCompact && (
                <Button variant="outline-compact" onClick={onShare}>
                  <ShareIcon />
                  Share
                </Button>
              )}
              {isCompact && onExpandToFull && (
                <Button variant="primary-compact" onClick={onExpandToFull}>
                  <ArrowUpRight className="h-4 w-4" />
                  View Full Details
                </Button>
              )}
              {!isCompact && onCollapseToCompact && (
                <Button variant="outline-compact" onClick={onCollapseToCompact}>
                  Back to Search
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Data Grid */}
      <div
        className={
          isCompact
            ? 'mb-3 grid grid-cols-2 gap-2'
            : 'mb-6 grid grid-cols-2 gap-3 md:grid-cols-4'
        }
      >
        <DataBlockV2>
          <DataBlockV2.Header>
            <DataBlockV2.Icon icon={DollarSign} color="accent" />
            <DataBlockV2.Label>
              {categoryConfig.primaryAmountLabel}
            </DataBlockV2.Label>
          </DataBlockV2.Header>
          <DataBlockV2.Value>
            {formatCompactCurrency(
              getPrimaryAmount(
                data.stats.category,
                award.financials as ExtendedFinancialData,
              ),
            ) || '$0'}
          </DataBlockV2.Value>
          {!isCompact && (
            <DataBlockV2.SubValue>
              {getPrimaryAmountSubtext(
                data.stats.category,
                award.financials as ExtendedFinancialData,
                data.financials.percentObligated,
              )}
            </DataBlockV2.SubValue>
          )}
        </DataBlockV2>
        <DataBlockV2>
          <DataBlockV2.Header>
            <DataBlockV2.Icon icon={User} color="success" />
            <DataBlockV2.Label>
              {categoryConfig.recipientLabel}
            </DataBlockV2.Label>
          </DataBlockV2.Header>
          <DataBlockV2.Value className="text-sm">
            {data.recipient.id ? (
              <RecipientLink
                hash={data.recipient.id}
                className="hover:text-blue-600 hover:underline dark:hover:text-blue-400"
              >
                {data.recipient.name || 'Unknown'}
              </RecipientLink>
            ) : (
              data.recipient.name || '—'
            )}
          </DataBlockV2.Value>
          {!isCompact && data.financials.currentAwardAmount > 0 && (
            <DataBlockV2.SubValue>
              {formatCompactCurrency(data.financials.currentAwardAmount)}
            </DataBlockV2.SubValue>
          )}
        </DataBlockV2>
        {/* Hide last two blocks in compact mode - only show amount and recipient */}
        {!isCompact && (
          <>
            <DataBlockV2>
              <DataBlockV2.Header>
                <DataBlockV2.Icon icon={Calendar} color="warning" />
                <DataBlockV2.Label>Period End</DataBlockV2.Label>
              </DataBlockV2.Header>
              <DataBlockV2.Value className="text-sm">
                {data.dates.endDateFormatted || '—'}
              </DataBlockV2.Value>
              {data.dates.potentialEndDateFormatted &&
                data.dates.potentialEndDate !== data.dates.endDate && (
                  <DataBlockV2.SubValue>
                    Potential: {data.dates.potentialEndDateFormatted}
                  </DataBlockV2.SubValue>
                )}
            </DataBlockV2>
            <DataBlockV2>
              <DataBlockV2.Header>
                <DataBlockV2.Icon icon={FileText} color="info" />
                <DataBlockV2.Label>
                  {categoryConfig.typeLabel}
                </DataBlockV2.Label>
              </DataBlockV2.Header>
              <DataBlockV2.Value className="text-sm">
                {data.stats.typeDescription || data.stats.type || '—'}
              </DataBlockV2.Value>
            </DataBlockV2>
          </>
        )}
      </div>

      {/* Compact Mode: Show description only */}
      {isCompact && (
        <div className="mt-2">
          <p className="line-clamp-2 text-xs leading-relaxed text-primary-600 dark:text-primary-400">
            {data.description || 'No description available.'}
          </p>
        </div>
      )}

      {/* Main Layout - Hidden in compact mode */}
      {!isCompact && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Main Content Column */}
          <div className="flex flex-col gap-4 lg:col-span-8">
            {/* Tabs */}
            <TabPanelV2
              value={activeTab}
              onValueChange={setActiveTab}
              defaultTab="overview"
            >
              <TabPanelV2.List>
                <TabPanelV2.Trigger value="overview">
                  Award Overview
                </TabPanelV2.Trigger>
                {(hasSolicitation || isLoadingSolicitation) && (
                  <TabPanelV2.Trigger value="solicitation">
                    <span className="flex items-center gap-1.5">
                      Solicitation
                      {isLoadingSolicitation && <Spinner className="h-3 w-3" />}
                    </span>
                  </TabPanelV2.Trigger>
                )}
                <TabPanelV2.Trigger value="transactions">
                  <span className="flex items-center gap-1">
                    {categoryConfig.transactionsTabLabel}
                    <span className="inline-flex h-4 min-w-[1.75rem] items-center justify-center">
                      {isLoadingTransactions && transactions.length === 0 ? (
                        <Spinner className="h-3.5 w-3.5" />
                      ) : (
                        <span className="tabular-nums">
                          ({totalTransactions || transactions.length})
                        </span>
                      )}
                    </span>
                  </span>
                </TabPanelV2.Trigger>
                {categoryConfig.hasSubawards && (
                  <TabPanelV2.Trigger value="subawards">
                    <span className="flex items-center gap-1">
                      Sub-Awards
                      <span className="inline-flex h-4 min-w-[1.75rem] items-center justify-center">
                        {isLoadingSubAwards && subAwards.length === 0 ? (
                          <Spinner className="h-3.5 w-3.5" />
                        ) : (
                          <span className="tabular-nums">
                            ({totalSubAwards || subAwards.length})
                          </span>
                        )}
                      </span>
                    </span>
                  </TabPanelV2.Trigger>
                )}
                {categoryConfig.hasOrders && categoryConfig.ordersTabLabel && (
                  <TabPanelV2.Trigger value="orders">
                    <span className="flex items-center gap-1">
                      {categoryConfig.ordersTabLabel}
                      <span className="inline-flex h-4 min-w-[1.75rem] items-center justify-center">
                        {isLoadingOrders && orders.length === 0 ? (
                          <Spinner className="h-3.5 w-3.5" />
                        ) : (
                          <span className="tabular-nums">
                            {/* Show expected contract count from idvActivity if available, otherwise loaded count */}
                            {/* Note: Orders are contracts only (category != 'idv'), not child IDVs */}
                            {idvActivity ? (
                              <>({idvActivity.summary.rollup_contract_count})</>
                            ) : (
                              <>({totalOrders || orders.length})</>
                            )}
                          </span>
                        )}
                      </span>
                    </span>
                  </TabPanelV2.Trigger>
                )}
                {categoryConfig.hasOrders && (
                  <TabPanelV2.Trigger value="activity">
                    <span className="flex items-center gap-1">
                      IDV Activity
                      {isLoadingIdvActivity && (
                        <Spinner className="ml-1 h-3.5 w-3.5" />
                      )}
                    </span>
                  </TabPanelV2.Trigger>
                )}
              </TabPanelV2.List>

              {/* Content Panels */}
              <TabPanelV2.Content value="overview">
                <div className="mt-2 space-y-4">
                  <CardV2 variant="accent">
                    <CardV2.Header>
                      <CardV2.HeaderIcon>
                        <FileText />
                      </CardV2.HeaderIcon>
                      <CardV2.HeaderTitle>Award Description</CardV2.HeaderTitle>
                    </CardV2.Header>
                    <CardV2.Content>
                      <p className="text-sm leading-relaxed text-primary-700 dark:text-primary-300">
                        {data.description ||
                          'No description available for this award.'}
                      </p>
                    </CardV2.Content>
                  </CardV2>

                  {/* Parent IDV Card (contract orders only) */}
                  {data.parentAward && (
                    <ParentIdvCard data={data.parentAward} />
                  )}

                  {/* Funding Gauge - Category-aware visualization */}
                  <FundingGaugeCard
                    financials={award.financials as ExtendedFinancialData}
                    category={award.category}
                    type={award.type}
                    childOrderData={idvChildAggregation}
                    isLoadingChildData={isLoadingIdvAggregation}
                  />

                  {/* Federal Accounts Section */}
                  <FederalAccountsCard accounts={data.federalAccounts} />

                  {/* Disaster/Emergency Funding Section (if DEFC data present) */}
                  <DisasterFundingCard
                    category={data.stats.category}
                    data={award.financials?.defcData}
                  />
                </div>
              </TabPanelV2.Content>

              {(hasSolicitation || isLoadingSolicitation) && (
                <TabPanelV2.Content value="solicitation">
                  <div className="mt-2">
                    {isLoadingSolicitation ? (
                      <div className="flex items-center justify-center py-12">
                        <Spinner className="h-6 w-6" />
                        <span className="ml-2 text-sm text-primary-500">
                          Loading solicitation data...
                        </span>
                      </div>
                    ) : data.solicitation || solicitationData ? (
                      <div className="space-y-4">
                        {/* Solicitation Header Card */}
                        <div className="rounded-lg border border-primary-200 bg-white p-4 shadow-sm dark:border-primary-700 dark:bg-primary-900">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 space-y-3">
                              {/* Header with badges */}
                              <div className="flex flex-wrap items-center gap-2">
                                <BadgeV2 variant="info" size="sm">
                                  {data.solicitation?.source ||
                                    solicitationData?.source}
                                </BadgeV2>
                                {data.solicitation?.noticeType && (
                                  <BadgeV2 variant="outline" size="sm">
                                    {data.solicitation.noticeType}
                                  </BadgeV2>
                                )}
                                {data.solicitation?.setAside && (
                                  <BadgeV2 variant="success" size="sm">
                                    {data.solicitation.setAside}
                                  </BadgeV2>
                                )}
                              </div>

                              {/* Solicitation number - clickable link */}
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-accent-500" />
                                {data.solicitationId ||
                                data.solicitation?.id ? (
                                  <Link
                                    href={`/federal-contracts/${data.solicitationId || data.solicitation?.id}`}
                                    className="font-mono text-sm font-medium text-accent-600 hover:text-blue-600 hover:underline dark:text-accent-400 dark:hover:text-blue-400"
                                  >
                                    {data.solicitation?.solicitationNumber ||
                                      solicitationData?.solicitationNumber}
                                  </Link>
                                ) : (
                                  <span className="font-mono text-sm font-medium text-primary-900 dark:text-primary-100">
                                    {data.solicitation?.solicitationNumber ||
                                      solicitationData?.solicitationNumber}
                                  </span>
                                )}
                              </div>

                              {/* Title - clickable link to federal-contract page */}
                              {data.solicitationId || data.solicitation?.id ? (
                                <Link
                                  href={`/federal-contracts/${data.solicitationId || data.solicitation?.id}`}
                                  className="group"
                                >
                                  <h3 className="text-base font-semibold text-primary-900 group-hover:text-blue-600 group-hover:underline dark:text-primary-100 dark:group-hover:text-blue-400">
                                    {data.solicitation?.title ||
                                      solicitationData?.title}
                                  </h3>
                                </Link>
                              ) : (
                                <h3 className="text-base font-semibold text-primary-900 dark:text-primary-100">
                                  {data.solicitation?.title ||
                                    solicitationData?.title}
                                </h3>
                              )}

                              {/* Dates row */}
                              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                                {(data.solicitation?.postedDateFormatted ||
                                  solicitationData?.postedDate) && (
                                  <div className="flex items-center gap-1.5 text-primary-600 dark:text-primary-200">
                                    <Calendar className="h-3.5 w-3.5" />
                                    <span className="text-xs font-medium uppercase text-primary-400">
                                      Posted:
                                    </span>
                                    <span>
                                      {data.solicitation?.postedDateFormatted ||
                                        formatDate(
                                          solicitationData?.postedDate,
                                        )}
                                    </span>
                                  </div>
                                )}
                                {(data.solicitation
                                  ?.responseDeadlineFormatted ||
                                  solicitationData?.responseDeadline) && (
                                  <div className="flex items-center gap-1.5 text-primary-600 dark:text-primary-200">
                                    <Clock className="h-3.5 w-3.5" />
                                    <span className="text-xs font-medium uppercase text-primary-400">
                                      Deadline:
                                    </span>
                                    <span>
                                      {data.solicitation
                                        ?.responseDeadlineFormatted ||
                                        formatDate(
                                          solicitationData?.responseDeadline,
                                        )}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Classification codes */}
                              {(data.solicitation?.naicsCode ||
                                data.solicitation?.pscCode) && (
                                <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-primary-100 pt-3 dark:border-primary-700">
                                  {data.solicitation?.naicsCode && (
                                    <div>
                                      <span className="text-xs font-semibold uppercase text-primary-400">
                                        NAICS
                                      </span>
                                      <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                                        {data.solicitation.naicsCode}
                                      </div>
                                      {data.solicitation.naicsName && (
                                        <div className="text-xs text-primary-500 dark:text-primary-200">
                                          {data.solicitation.naicsName}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {data.solicitation?.pscCode && (
                                    <div>
                                      <span className="text-xs font-semibold uppercase text-primary-400">
                                        PSC
                                      </span>
                                      <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                                        {data.solicitation.pscCode}
                                      </div>
                                      {data.solicitation.pscName && (
                                        <div className="text-xs text-primary-500 dark:text-primary-200">
                                          {data.solicitation.pscName}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Description */}
                        {(data.solicitation?.description ||
                          solicitationData?.description) && (
                          <CardV2 variant="accent">
                            <CardV2.Header>
                              <CardV2.HeaderIcon>
                                <FileText />
                              </CardV2.HeaderIcon>
                              <CardV2.HeaderTitle>
                                Solicitation Description
                              </CardV2.HeaderTitle>
                            </CardV2.Header>
                            <CardV2.Content>
                              <p className="text-sm leading-relaxed text-primary-700 dark:text-primary-300">
                                {data.solicitation?.description ||
                                  solicitationData?.description}
                              </p>
                            </CardV2.Content>
                          </CardV2>
                        )}

                        {/* Point of Contact */}
                        {data.solicitationContact && (
                          <CardV2 variant="accent">
                            <CardV2.Header>
                              <CardV2.HeaderIcon>
                                <User />
                              </CardV2.HeaderIcon>
                              <CardV2.HeaderTitle>
                                Point of Contact
                              </CardV2.HeaderTitle>
                            </CardV2.Header>
                            <CardV2.Content>
                              <div className="space-y-2">
                                {data.solicitationContact.id ? (
                                  <Link
                                    href={`/agencies/personnel/usaspending/${data.solicitationContact.id}`}
                                    className="group block"
                                  >
                                    <div className="text-[13px] font-medium text-accent-600 group-hover:underline dark:text-accent-400">
                                      {data.solicitationContact.name}
                                    </div>
                                  </Link>
                                ) : (
                                  <div className="text-[13px] font-medium text-primary-900 dark:text-primary-100">
                                    {data.solicitationContact.name}
                                  </div>
                                )}
                                {data.solicitationContact.title && (
                                  <div className="text-xs text-primary-500 dark:text-primary-200">
                                    {data.solicitationContact.title}
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
                                  {data.solicitationContact.email && (
                                    <a
                                      href={`mailto:${data.solicitationContact.email}`}
                                      className="flex items-center gap-1.5 text-xs text-accent-600 hover:underline dark:text-accent-400"
                                    >
                                      <Mail className="h-3 w-3" />
                                      {data.solicitationContact.email}
                                    </a>
                                  )}
                                  {data.solicitationContact.phone && (
                                    <a
                                      href={`tel:${data.solicitationContact.phone}`}
                                      className="flex items-center gap-1.5 text-xs text-accent-600 hover:underline dark:text-accent-400"
                                    >
                                      <Phone className="h-3 w-3" />
                                      {data.solicitationContact.phone}
                                    </a>
                                  )}
                                </div>
                              </div>
                            </CardV2.Content>
                          </CardV2>
                        )}
                      </div>
                    ) : (
                      <div className="py-12 text-center text-sm text-primary-500 dark:text-primary-200">
                        No solicitation data available for this award.
                      </div>
                    )}
                  </div>
                </TabPanelV2.Content>
              )}

              <TabPanelV2.Content value="transactions">
                <div className="mt-2">
                  {isLoadingTransactions && transactions.length === 0 ? (
                    <DataTableV2.Skeleton rowCount={5} columnCount={5} />
                  ) : transactions.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-primary-200 bg-primary-50 py-8 text-center text-sm text-primary-500 dark:border-primary-700 dark:bg-primary-800/50 dark:text-primary-200">
                      No transaction history available for this award.
                    </div>
                  ) : (
                    <DataTableV2
                      data={transactions}
                      totalCount={totalTransactions}
                      columns={transactionColumns}
                      columnOrder={transactionPreferences.columnOrder}
                      columnVisibility={transactionPreferences.columnVisibility}
                      isLoading={isLoadingTransactions}
                      isFetchingNextPage={isFetchingMoreTransactions}
                      hasNextPage={hasMoreTransactions}
                      fetchNextPage={onLoadMoreTransactions}
                      fetchAllPages={onLoadAllTransactions}
                      isFullyLoaded={isTransactionsFullyLoaded}
                      estimatedRowHeight={48}
                      defaultSorting={[{ id: 'actionDate', desc: true }]}
                    >
                      <DataTableV2.ScrollArea
                        maxHeight={600}
                        className="persistent-scrollbar"
                      >
                        <DataTableV2.Header sticky />
                        <DataTableV2.VirtualBody
                          renderRow={(row) => (
                            <TransactionRow row={row as Row<Transaction>} />
                          )}
                          overscan={10}
                          useParentScroll
                        />
                      </DataTableV2.ScrollArea>
                    </DataTableV2>
                  )}
                  {/* Column selector below table */}
                  <div className="mt-3 flex items-center justify-end">
                    <ColumnSelector
                      columns={transactionColumnDefs}
                      preferences={transactionPreferences}
                    />
                  </div>
                </div>
              </TabPanelV2.Content>

              {categoryConfig.hasSubawards && (
                <TabPanelV2.Content value="subawards">
                  <div className="mt-2">
                    {isLoadingSubAwards && subAwards.length === 0 ? (
                      <DataTableV2.Skeleton rowCount={5} columnCount={5} />
                    ) : subAwards.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-primary-200 bg-primary-50 py-8 text-center text-sm text-primary-500 dark:border-primary-700 dark:bg-primary-800/50 dark:text-primary-200">
                        No sub-awards available for this award.
                      </div>
                    ) : (
                      <DataTableV2
                        data={subAwards}
                        totalCount={totalSubAwards}
                        columns={subAwardColumns}
                        columnOrder={subAwardPreferences.columnOrder}
                        columnVisibility={subAwardPreferences.columnVisibility}
                        isLoading={isLoadingSubAwards}
                        isFetchingNextPage={isFetchingMoreSubAwards}
                        hasNextPage={hasMoreSubAwards}
                        fetchNextPage={onLoadMoreSubAwards}
                        fetchAllPages={onLoadAllSubAwards}
                        isFullyLoaded={isSubAwardsFullyLoaded}
                        estimatedRowHeight={48}
                        defaultSorting={[{ id: 'date', desc: true }]}
                      >
                        <DataTableV2.ScrollArea
                          maxHeight={600}
                          className="persistent-scrollbar"
                        >
                          <DataTableV2.Header sticky />
                          <DataTableV2.VirtualBody
                            renderRow={(row) => (
                              <SubAwardRow row={row as Row<SubAward>} />
                            )}
                            overscan={10}
                            useParentScroll
                          />
                        </DataTableV2.ScrollArea>
                      </DataTableV2>
                    )}
                    {/* Column selector below table */}
                    <div className="mt-3 flex items-center justify-end">
                      <ColumnSelector
                        columns={subAwardColumnDefs}
                        preferences={subAwardPreferences}
                      />
                    </div>
                  </div>
                </TabPanelV2.Content>
              )}

              {categoryConfig.hasOrders && (
                <TabPanelV2.Content value="orders">
                  <div className="mt-2">
                    {isLoadingOrders && orders.length === 0 ? (
                      <DataTableV2.Skeleton rowCount={5} columnCount={5} />
                    ) : orders.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-primary-200 bg-primary-50 py-8 text-center dark:border-primary-700 dark:bg-primary-800/50">
                        {idvActivity &&
                        idvActivity.summary.rollup_contract_count > 0 ? (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
                              {idvActivity.summary.rollup_contract_count} orders
                              pending import
                            </p>
                            <p className="text-xs text-primary-500 dark:text-primary-200">
                              Historical order data is being processed and will
                              appear here once available.
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-primary-500 dark:text-primary-200">
                            No task/delivery orders found for this IDV.
                          </p>
                        )}
                      </div>
                    ) : (
                      <DataTableV2
                        data={orders}
                        totalCount={totalOrders}
                        columns={orderColumns}
                        columnOrder={orderPreferences.columnOrder}
                        columnVisibility={orderPreferences.columnVisibility}
                        isLoading={isLoadingOrders}
                        isFetchingNextPage={isFetchingMoreOrders}
                        hasNextPage={hasMoreOrders}
                        fetchNextPage={onLoadMoreOrders}
                        fetchAllPages={onLoadAllOrders}
                        isFullyLoaded={isOrdersFullyLoaded}
                        estimatedRowHeight={48}
                        defaultSorting={[{ id: 'action_date', desc: true }]}
                      >
                        <DataTableV2.ScrollArea
                          maxHeight={600}
                          className="persistent-scrollbar"
                        >
                          <DataTableV2.Header sticky />
                          <DataTableV2.VirtualBody
                            renderRow={(row) => (
                              <OrderRow row={row as Row<ApiOrderItem>} />
                            )}
                            overscan={10}
                            useParentScroll
                          />
                        </DataTableV2.ScrollArea>
                      </DataTableV2>
                    )}
                    {/* Column selector below table */}
                    <div className="mt-3 flex items-center justify-end">
                      <ColumnSelector
                        columns={ORDER_COLUMN_DEFS}
                        preferences={orderPreferences}
                      />
                    </div>
                  </div>
                </TabPanelV2.Content>
              )}

              {categoryConfig.hasOrders && (
                <TabPanelV2.Content value="activity">
                  <div className="mt-2">
                    {isLoadingIdvActivity ? (
                      <div className="flex h-64 items-center justify-center">
                        <Spinner className="h-8 w-8" />
                      </div>
                    ) : hasActivityData(idvActivity) ? (
                      <div className="space-y-4">
                        {/* Summary Stats */}
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                          <DataBlockV2>
                            <DataBlockV2.Header>
                              <DataBlockV2.Label>
                                Direct Contracts
                              </DataBlockV2.Label>
                            </DataBlockV2.Header>
                            <DataBlockV2.Value>
                              {idvActivity.summary.direct_contract_count}
                            </DataBlockV2.Value>
                          </DataBlockV2>
                          <DataBlockV2>
                            <DataBlockV2.Header>
                              <DataBlockV2.Label>Direct IDVs</DataBlockV2.Label>
                            </DataBlockV2.Header>
                            <DataBlockV2.Value>
                              {idvActivity.summary.direct_idv_count}
                            </DataBlockV2.Value>
                          </DataBlockV2>
                          <DataBlockV2>
                            <DataBlockV2.Header>
                              <DataBlockV2.Label>
                                Total Contracts
                              </DataBlockV2.Label>
                            </DataBlockV2.Header>
                            <DataBlockV2.Value>
                              {idvActivity.summary.rollup_contract_count}
                            </DataBlockV2.Value>
                          </DataBlockV2>
                          <DataBlockV2>
                            <DataBlockV2.Header>
                              <DataBlockV2.Label>
                                Total Obligation
                                <ObligationMismatchTooltip
                                  summary={idvActivity.summary}
                                />
                              </DataBlockV2.Label>
                            </DataBlockV2.Header>
                            <DataBlockV2.Value>
                              {formatCompactCurrency(
                                idvActivity.summary.rollup_total_obligation,
                              )}
                            </DataBlockV2.Value>
                          </DataBlockV2>
                        </div>
                        {/* Sankey Visualization */}
                        <IdvActivitySankey
                          data={idvActivity}
                          parentPiid={data.piid ?? 'IDV'}
                          height={Math.max(
                            400,
                            Math.min(800, idvActivity.nodes.length * 40),
                          )}
                        />
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-primary-200 bg-primary-50 py-8 text-center text-sm text-primary-500 dark:border-primary-700 dark:bg-primary-800/50 dark:text-primary-200">
                        No activity data available for this IDV.
                      </div>
                    )}
                  </div>
                </TabPanelV2.Content>
              )}
            </TabPanelV2>

            {/* Procurement Details (FPDS contracts only) - Below tabs */}
            {data.procurementDetails && (
              <ProcurementDetailsPanel data={data.procurementDetails} />
            )}

            {/* Program Details (Assistance awards: grants, loans, etc.) */}
            <ProgramDetailsPanel
              category={data.stats.category}
              data={{
                cfdaNumber: data.codes.cfdaNumber ?? undefined,
                cfdaTitle: data.codes.cfdaTitle ?? undefined,
                cfdaNumbers: data.codes.cfdaNumbers,
                // Enhanced program details from CFDA lookup
                objectives: data.programDetails?.objectives,
                applicantEligibility: data.programDetails?.applicantEligibility,
                typesOfAssistance: data.programDetails?.typesOfAssistance,
                websiteUrl: data.programDetails?.websiteAddress,
              }}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-4 lg:col-span-4">
            {/* Key Dates Timeline Widget */}
            {data.timeline.length > 0 && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={Clock}>
                  Key Dates
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  <TimelineV2>
                    {data.timeline.map((event) => (
                      <TimelineV2.Node
                        key={event.id}
                        date={event.date}
                        label={event.label}
                        status={event.status}
                      />
                    ))}
                  </TimelineV2>
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}

            {/* Awarding Agency Widget */}
            {(data.awardingAgency.name ||
              data.awardingAgency.subTier ||
              data.awardingAgency.office) && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={Building2}>
                  Awarding Agency
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  {data.awardingAgency.name && (
                    <div>
                      <span className="mb-0.5 block text-xs font-semibold uppercase text-primary-400">
                        Department
                      </span>
                      {data.awardingAgency.id ? (
                        <Link
                          href={`/agencies/usaspending/${data.awardingAgency.id}`}
                          className="group block"
                        >
                          <div className="text-[13px] font-medium text-accent-600 group-hover:text-blue-600 group-hover:underline dark:text-accent-400 dark:group-hover:text-blue-400">
                            {data.awardingAgency.name}
                          </div>
                        </Link>
                      ) : (
                        <div className="text-[13px] font-medium text-primary-900 dark:text-primary-100">
                          {data.awardingAgency.name}
                        </div>
                      )}
                    </div>
                  )}
                  {data.awardingAgency.subTier && (
                    <div>
                      <span className="mb-0.5 block text-xs font-semibold uppercase text-primary-400">
                        Sub-Tier
                      </span>
                      <div className="text-[13px] font-medium text-primary-900 dark:text-primary-100">
                        {data.awardingAgency.subTier}
                      </div>
                    </div>
                  )}
                  {data.awardingAgency.office && (
                    <div>
                      <span className="mb-0.5 block text-xs font-semibold uppercase text-primary-400">
                        Office
                      </span>
                      <div className="text-[13px] font-medium text-primary-900 dark:text-primary-100">
                        {data.awardingAgency.office}
                      </div>
                    </div>
                  )}
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}

            {/* Recipient Details Widget */}
            {data.recipient.name && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={User}>
                  {categoryConfig.recipientDetailsTitle}
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  <div>
                    <span className="mb-0.5 block text-xs font-semibold uppercase text-primary-400">
                      {categoryConfig.recipientLabel}
                    </span>
                    {data.recipient.id ? (
                      <RecipientLink
                        hash={data.recipient.id}
                        className="group block"
                      >
                        <div className="text-[13px] font-medium text-accent-600 group-hover:text-blue-600 group-hover:underline dark:text-accent-400 dark:group-hover:text-blue-400">
                          {data.recipient.name}
                        </div>
                      </RecipientLink>
                    ) : (
                      <div className="text-[13px] font-medium text-primary-900 dark:text-primary-100">
                        {data.recipient.name}
                      </div>
                    )}
                    {data.recipient.uei && (
                      <div className="mt-0.5 font-mono text-xs text-primary-400">
                        UEI: {data.recipient.uei}
                      </div>
                    )}
                  </div>

                  {/* Parent Recipient */}
                  {data.recipient.parentName && (
                    <div>
                      <span className="mb-0.5 block text-xs font-semibold uppercase text-primary-400">
                        Parent Organization
                      </span>
                      {data.recipient.parentId ? (
                        <RecipientLink
                          hash={data.recipient.parentId}
                          level="P"
                          className="group block"
                        >
                          <div className="text-[13px] font-medium text-accent-600 group-hover:text-blue-600 group-hover:underline dark:text-accent-400 dark:group-hover:text-blue-400">
                            {data.recipient.parentName}
                          </div>
                        </RecipientLink>
                      ) : (
                        <div className="text-[13px] font-medium text-primary-700 dark:text-primary-300">
                          {data.recipient.parentName}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Full Address */}
                  {(data.recipient.address.streetAddress ||
                    data.recipient.location) && (
                    <div>
                      <span className="mb-0.5 block text-xs font-semibold uppercase text-primary-400">
                        Address
                      </span>
                      <div className="text-[13px] text-primary-700 dark:text-primary-300">
                        {data.recipient.address.streetAddress && (
                          <div>{data.recipient.address.streetAddress}</div>
                        )}
                        <div>
                          {[
                            data.recipient.address.city,
                            data.recipient.address.state,
                            data.recipient.address.zip,
                          ]
                            .filter(Boolean)
                            .join(', ') || data.recipient.location}
                        </div>
                        {data.recipient.address.country &&
                          data.recipient.address.country !== 'USA' &&
                          data.recipient.address.country !== 'US' && (
                            <div>{data.recipient.address.country}</div>
                          )}
                      </div>
                      {data.recipient.address.congressionalDistrict && (
                        <div className="mt-0.5 text-xs text-primary-500 dark:text-primary-200">
                          Congressional District:{' '}
                          {data.recipient.address.congressionalDistrict}
                        </div>
                      )}
                    </div>
                  )}

                  {data.recipient.businessTypes.length > 0 && (
                    <div>
                      <span className="mb-1 block text-xs font-semibold uppercase text-primary-400">
                        Business Categories
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {(businessTypesExpanded
                          ? data.recipient.businessTypes
                          : data.recipient.businessTypes.slice(0, 5)
                        ).map((type) => (
                          <span
                            key={type}
                            className="rounded border border-primary-200 bg-primary-100 px-1.5 py-0.5 text-xs font-medium text-primary-600 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-200"
                          >
                            {formatBusinessType(type)}
                          </span>
                        ))}
                        {data.recipient.businessTypes.length > 5 && (
                          <button
                            onClick={() =>
                              setBusinessTypesExpanded(!businessTypesExpanded)
                            }
                            className="text-xs font-medium text-accent-600 hover:underline dark:text-accent-400"
                          >
                            {businessTypesExpanded
                              ? 'Show less'
                              : `+${data.recipient.businessTypes.length - 5} more`}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}

            {/* Officers Widget (from recipient) */}
            {data.officers.length > 0 && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={Users}>
                  Key Officers
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  {data.officers.slice(0, 5).map((officer, idx) => (
                    <div key={officer.id || idx}>
                      {officer.id ? (
                        <Link
                          href={`/agencies/personnel/usaspending/${officer.id}`}
                          className="group block"
                        >
                          <div className="text-[13px] font-medium text-accent-600 group-hover:underline dark:text-accent-400">
                            {officer.name}
                          </div>
                        </Link>
                      ) : (
                        <div className="text-[13px] font-medium text-primary-900 dark:text-primary-100">
                          {officer.name}
                        </div>
                      )}
                      {(officer.title || officer.role) && (
                        <div className="text-xs text-primary-500 dark:text-primary-200">
                          {officer.title || officer.role}
                        </div>
                      )}
                    </div>
                  ))}
                  {data.officers.length > 5 && (
                    <div className="text-xs text-primary-400">
                      +{data.officers.length - 5} more
                    </div>
                  )}
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}

            {/* Place of Performance Widget */}
            {data.placeOfPerformance.formatted && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={MapPin}>
                  Place of Performance
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  <PlaceOfPerformanceV2 data={data.placeOfPerformance} />
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}

            {/* Classification Codes Widget */}
            {(data.codes.naicsCode || data.codes.pscCode) && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={Tag}>
                  Classification Codes
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  {data.codes.naicsCode && (
                    <div>
                      <span className="mb-0.5 block text-xs font-semibold uppercase text-primary-400">
                        NAICS Code
                      </span>
                      <div className="text-[13px] font-medium text-primary-900 dark:text-primary-100">
                        {data.codes.naicsCode}
                      </div>
                      {data.codes.naicsDescription && (
                        <div className="mt-0.5 text-xs text-primary-500 dark:text-primary-200">
                          {data.codes.naicsDescription}
                        </div>
                      )}
                    </div>
                  )}
                  {data.codes.pscCode && (
                    <div>
                      <span className="mb-0.5 block text-xs font-semibold uppercase text-primary-400">
                        PSC Code
                      </span>
                      <div className="text-[13px] font-medium text-primary-900 dark:text-primary-100">
                        {data.codes.pscCode}
                      </div>
                      {data.codes.pscDescription && (
                        <div className="mt-0.5 text-xs text-primary-500 dark:text-primary-200">
                          {data.codes.pscDescription}
                        </div>
                      )}
                    </div>
                  )}
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}
          </div>
        </div>
      )}
    </>
  )
}

AwardDisplayV2.displayName = 'AwardDisplayV2'

export { AwardDisplayV2 }
export { AwardDisplayV2Skeleton } from './Skeleton'
export {
  getCategoryConfig,
  categoryHasSubawards,
  categoryHasOrders,
  type CategoryConfig,
} from './categoryConfig'
export type { AwardDisplayV2Props }
